import { useMemo } from 'react'

import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { UserSummaryModel } from '../../client/fleet-management'
import useManagedOrgTree from './organizations'
import useDataManager from './useDataManager'

/**
 * Users across the current org and its descendant orgs, sourced from the
 * managed IndexedDB cache (backed by `GET /api/v1/users`). Use this for
 * recipient/assignee pickers that must resolve users belonging to a child org.
 */
export default function useManagedOrgTreeUsers() {
  const { orgTreeUsers } = useDataManager()
  const { data, isLoading, isFetching } = orgTreeUsers
  const { isLoading: isOrgTreeLoading, isFetching: isOrgTreeFetching } = useManagedOrgTree()

  return {
    users: useFilteredOrgTreeUsers(data),
    isLoading: isLoading || isOrgTreeLoading,
    isFetching: isFetching || isOrgTreeFetching
  }
}

/**
 * `GET /api/v1/users` resolves its org tree from the requesting user's own organization,
 * not the effective one, so the cached set spans the principal org tree however the
 * caller is cloaked. Narrowing to the effective org happens here, against the same
 * `descendantIds` index that gates site and vehicle visibility — so an org outside the
 * effective subtree contributes no recipients.
 */
function useFilteredOrgTreeUsers(data: UserSummaryModel[]) {
  const currentOrgId = useCurrentOrgId()
  const { orgTree } = useManagedOrgTree()

  return useMemo(() => {
    if (!currentOrgId) return data

    const descendants = orgTree.indexes.descendantIds.get(currentOrgId)
    return data.filter(
      (user) => user.organizationId === currentOrgId || descendants?.has(user.organizationId ?? '')
    )
  }, [data, currentOrgId, orgTree])
}
